import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../Data/ReduxViewModels/WorksheetEditor';
import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../Layout';
import { NuclearDensityGaugeMode } from '../../types';
import { NfdReading } from './NuclearDensityReading';

const Section: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, prefixName }) => {
  const dispatch = useDispatch();
  const enterTestCounts = useSelector(
    pipe(createWorksheetSelector!('Measurements.EnterTestCounts'), valueOrDefault(false))
  );

  const confirmGaugeModeChange = useSelector(
    combine(
      [
        createWorksheetSelector!('Measurements.NuclearDensityGaugeMode'),
        createWorksheetSelector!('Measurements.Depth'),
      ],
      (gaugeMode, depth) => gaugeMode === NuclearDensityGaugeMode.DirectTransmission && hasValue(depth)
    )
  );

  /*
    For handling a checkbox that maps to an integer.
  */
  const numberOfReadings = useSelector(
    pipe(createWorksheetSelector!('Measurements.NumberOfReadings'), valueOrDefault(1))
  );
  const handleAdditionalReadingChange = ({ data: { value: checked } }: any) => {
    const newValue = checked ? 2 : 1;
    dispatch(actions.setValue({ name: prefixName!('Measurements.NumberOfReadings'), value: newValue, sync: true }));
  };
  const allowDepthInput = useSelector(createWorksheetSelector!('Measurements.AllowEnterProbeDepth')) as boolean;

  return (
    <PageSection heading="Measurements">
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          label="Gauge Mode"
          name="Measurements.NuclearDensityGaugeMode"
          options={[
            { label: 'Backscatter', value: NuclearDensityGaugeMode.Backscatter },
            { label: 'Direct Transmission', value: NuclearDensityGaugeMode.DirectTransmission },
          ]}
          simpleValue
          confirm={confirmGaugeModeChange}
          confirmText={'This action will delete the entered Probe Depth. Are you sure you want to continue?'}
        />
        <Field
          input={allowDepthInput ? <NumericInput /> : <TextOutput />}
          name="Measurements.Depth"
          blankReplacement={allowDepthInput ? undefined : 'N/A'}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field input={<Checkbox />} label="Enter Test Counts" name="Measurements.EnterTestCounts" />
      </ListFieldSection>
      <NamePrefixModifier appendNamePrefix="Measurements.Readings[0]">
        <NfdReading enterTestCounts={enterTestCounts} heading="" />
      </NamePrefixModifier>
      <ListFieldSection>
        <Field
          input={<Checkbox />}
          label="Obtain Additional Reading at 90°"
          onChange={handleAdditionalReadingChange}
          value={numberOfReadings === 2}
          additionalMessagePaths={['Measurements.NumberOfReadings']}
          confirm={numberOfReadings === 2}
          confirmText="All data entered for the additional reading will be lost. Delete the additional reading?"
        />
      </ListFieldSection>
      {numberOfReadings === 2 && (
        <>
          <NamePrefixModifier appendNamePrefix="Measurements.Readings[1]">
            <NfdReading enterTestCounts={enterTestCounts} heading="Reading at 90°" />
          </NamePrefixModifier>
          <NamePrefixModifier appendNamePrefix="Measurements.AverageReadings">
            <NfdReading enterTestCounts={enterTestCounts} heading="Averages" readonly />
          </NamePrefixModifier>
        </>
      )}
    </PageSection>
  );
};

const ArizMeasurementSection = withInjectedProps(Section);

export { ArizMeasurementSection };
export default ArizMeasurementSection;
